/**
 * CoordinateTransformFactory - Creates unified coordinate transforms for rendering
 *
 * Extracted from EdgeDocsCanvas.V3.tsx Phase 4 Step 7
 * 
 * This utility creates the Transform object that maps WASM world coordinates
 * (96 DPI) to canvas CSS coordinates. The transform is used by:
 * - Glyph rendering
 * - Caret positioning
 * - Selection highlighting
 * - Hit testing (click-to-position)
 */

import { makeTransform, Transform } from '../../../../utils/Transform';
import { getPageMetricsPx } from '../../../../utils/PageMetrics';
import type { PageSetup } from '../../../../core/types/Typesetter';

/**
 * Options for creating a render transform
 */
export interface RenderTransformOptions {
  wasmPageSetup: PageSetup;
  canvasCssWidth: number;
  canvasCssHeight: number;
  dpr: number;
  enableLogging?: boolean;
}

/**
 * Result from transform creation
 */
export interface RenderTransformResult {
  transform: Transform;
  pageMetrics: {
    pageWidthPx: number;
    pageHeightPx: number;
    contentX: number;
    contentY: number;
    contentW: number;
    contentH: number;
  };
}

/**
 * Create a unified coordinate transform for rendering
 * 
 * Maps WASM world space (96 DPI pixels) to canvas CSS space.
 * The transform ensures pixel-perfect alignment across:
 * - Glyph rendering
 * - Caret positioning
 * - Selection highlighting
 * - Hit testing
 *
 * @param options - Transform creation options
 * @returns Transform object and page metrics
 */
export function createRenderTransform(
  options: RenderTransformOptions
): RenderTransformResult {
  const { wasmPageSetup, canvasCssWidth, canvasCssHeight, dpr, enableLogging = false } = options;
  
  const debugLog = enableLogging ? console.log.bind(console) : () => {};
  
  debugLog('🎨 [CoordinateTransform] ======= CREATING UNIFIED COORDINATE TRANSFORM =======');
  
  // Extract page dimensions from WASM PageSetup (in world pixels, 96 DPI)
  const pageMetrics = getPageMetricsPx(wasmPageSetup);
  debugLog('🎨 [CoordinateTransform] Page metrics from WASM:', {
    pageWidth: pageMetrics.pageWidthPx.toFixed(2),
    pageHeight: pageMetrics.pageHeightPx.toFixed(2),
    contentRect: `${pageMetrics.contentX.toFixed(2)}, ${pageMetrics.contentY.toFixed(2)}, ${pageMetrics.contentW.toFixed(2)}x${pageMetrics.contentH.toFixed(2)}`
  });
  
  // Create transform: World Space (WASM 96 DPI px) → Canvas Space (CSS px)
  const transform = makeTransform({
    pageWidthPx: pageMetrics.pageWidthPx,
    pageHeightPx: pageMetrics.pageHeightPx,
    canvasCssWidth,
    canvasCssHeight,
    zoom: 1.0, // No zoom for now, scale prop is already applied to canvas size
    dpr,
    pageOriginX: 0, // No centering offset for now
    pageOriginY: 0
  });
  
  debugLog('🎨 [CoordinateTransform] Transform created:', {
    scale: transform.scale.toFixed(6),
    dpr: transform.dpr,
    offsetX: transform.offsetX,
    offsetY: transform.offsetY
  });
  
  debugLog(`🎨 [CoordinateTransform] ======= COORDINATE SYSTEM MAPPING =======`);
  debugLog(`   World space (WASM): ${pageMetrics.pageWidthPx.toFixed(2)}px @ 96 DPI`);
  debugLog(`   Canvas space (CSS): ${canvasCssWidth.toFixed(2)}px`);
  debugLog(`   Transform scale: ${transform.scale.toFixed(6)}`);
  debugLog(`   Device pixel ratio: ${dpr}`);
  debugLog(`   ===========================================`);
  
  return { transform, pageMetrics };
}

/**
 * Expose transform to window for debugging
 * 
 * Makes the transform accessible via window.__DEBUG_TRANSFORM__
 * for click position debugging in the browser console.
 *
 * @param transform - The transform to expose
 */
export function exposeTransformForDebugging(transform: Transform): void {
  (window as any).__DEBUG_TRANSFORM__ = transform;
}
